import { Box, Stack, Typography, useTheme } from "@mui/material";
import { Tag } from "Components/Analyses/_Shared/Tag";
import { HStack } from "Components/_Shared/Core/Stack";
import { ReactComponent as BusinessIcon } from "Components/_Shared/Icons/Assets/business.svg";
import { ReactComponent as ChildIcon } from "Components/_Shared/Icons/Assets/child.svg";
import { ReactComponent as FinancialSuccessIcon } from "Components/_Shared/Icons/Assets/financial-success.svg";
import { ReactComponent as GrandfatherIcon } from "Components/_Shared/Icons/Assets/grandfather.svg";
import { ReactComponent as HomeIcon } from "Components/_Shared/Icons/Assets/home.svg";
import { ReactComponent as RefundIcon } from "Components/_Shared/Icons/Assets/refund.svg";
import { ReactComponent as StackIcon } from "Components/_Shared/Icons/Assets/stack.svg";
import { ReactComponent as TargetIcon } from "Components/_Shared/Icons/Assets/target.svg";
import { ReactComponent as UserIcon } from "Components/_Shared/Icons/Assets/user-male.svg";
import { type AnalysisClientItem, type Income, IncomeIncomeType } from "Infrastructure/ApiZfp/Schema";
import { FeatureFlag, useFeatureFlag } from "Infrastructure/Hooks/useFeatureFlag";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatClientName } from "Infrastructure/Utils/ClientUtils";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { type FC, useState } from "react";
import { EditIncomeDrawer } from "./EditIncomeDrawer";
import { getIncomeTypeResource } from "./IncomeTypeTile";

type Props = {
  income: Income;
  clients: AnalysisClientItem[];
  /** Replaces the tile's own edit drawer. */
  onEdit?: () => void;
};

const getIcon = (incomeType: IncomeIncomeType) => {
  switch (incomeType) {
    case IncomeIncomeType.employment:
      return <BusinessIcon />;
    case IncomeIncomeType.self_employment:
      return <FinancialSuccessIcon />;
    case IncomeIncomeType.rental:
      return <HomeIcon />;
    case IncomeIncomeType.parental:
      return <ChildIcon />;
    case IncomeIncomeType.pension:
      return <GrandfatherIcon />;
    case IncomeIncomeType.disability:
      return <GrandfatherIcon />;
    case IncomeIncomeType.orphan_or_widow_pension:
      return <RefundIcon />;
    case IncomeIncomeType.other:
      return <TargetIcon />;
  }
};

export const IncomeTile: FC<Props> = ({ income, clients, onEdit }) => {
  const { t } = useResource();
  const theme = useTheme();

  const isBudgetBulkEntryEnabled = useFeatureFlag(FeatureFlag.BudgetBulkEntry);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);

  const client = clients.find((client) => client.guid === income.clientGuid);
  const totalAmountValue = income.incomeAmounts.reduce((sum, incomeAmount) => sum + incomeAmount.amount.value, 0);
  const currencyCode = income.incomeAmounts[0]?.amount.currencyCode ?? "CZK";
  // several other incomes of one client would otherwise differ only by their amount
  const otherIncomeNames =
    isBudgetBulkEntryEnabled && income.incomeType === IncomeIncomeType.other
      ? income.incomeAmounts
          .map((incomeAmount) => incomeAmount.name)
          .filter(Boolean)
          .join(", ")
      : "";
  const title = [t(getIncomeTypeResource(income.incomeType)), otherIncomeNames].filter(Boolean).join(" · ");

  return (
    <>
      <Stack
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        gap={Spacing.L}
        sx={{
          border: `1px solid ${theme.colors.borderLight}`,
          borderRadius: theme.spacing(Radius.M),
          padding: theme.spacing(Spacing.L),
          boxShadow: theme.shadows[1],
          background: theme.colors.white,
          cursor: "pointer",
        }}
        onClick={onEdit ?? (() => setEditDrawerOpen(true))}
      >
        {getIcon(income.incomeType)}

        <Stack spacing={Spacing.M} width="100%" direction="column" justifyContent="flex-start" mt={theme.spacing(Spacing.XS)}>
          <Typography variant="body2" color={theme.colors.textPrimary} fontWeight={600}>
            {title}
          </Typography>

          <HStack justifyContent="flex-start" alignItems="center" gap={Spacing.M}>
            {client && (
              <Tag
                variant="solid"
                color="info"
                title={formatClientName(client.firstName, client.surname, null)}
                icon={<UserIcon width={12} height={12} />}
              />
            )}
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="flex-start"
              alignItems="center"
              gap={Spacing.S}
              sx={{
                height: "22px",
                background: theme.colors.gray,
                padding: `${theme.spacing(Spacing.XS)} ${theme.spacing(Spacing.M)}`,
                borderRadius: theme.spacing(Radius.XL),
              }}
            >
              <StackIcon />

              <Typography variant="caption" color={theme.colors.textPrimary} fontWeight={500}>
                {formatCurrency(totalAmountValue, currencyCode)}
              </Typography>
            </Box>
          </HStack>
        </Stack>
      </Stack>

      {!onEdit && (
        <EditIncomeDrawer open={editDrawerOpen} anchor="right" onClose={() => setEditDrawerOpen(false)} clients={clients} income={income} />
      )}
    </>
  );
};
